import { faEdit, faGripDotsVertical } from '@fortawesome/pro-regular-svg-icons';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { DndSortableList } from 'src/components/DndSortableList';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { PlatformTab } from 'src/models/platform/_types';

import { FormTextField } from '../Forms/Fields/FormTextField';


interface MenuSectionProps {
  title: string;
  items: PlatformTab[];
  setItems: (items: PlatformTab[]) => void;
  editingItemId: string | null;
  setEditingItemId: (id: string | null) => void;
  isLoading?: boolean;
  disableTab: boolean;
  canDisableTab: boolean;
  setDisableTab: (value: boolean) => void | (() => void);
  tabArrayKey: string | null;
}

export const MenuSection: React.FC<MenuSectionProps> = ({
  title,
  items,
  setItems,
  editingItemId,
  setEditingItemId,
  isLoading,
  disableTab,
  canDisableTab,
  setDisableTab,
  tabArrayKey,
}) => {
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  const handleToggleItem = (itemId: string) => {
    const item = items.find((item) => item.id === itemId);
    const enabledCount = items.filter((item) => item.enabled).length;

    // Prevent disabling the last enabled item
    if (item?.enabled && enabledCount <= 1) {
      return;
    }

    setItems(
      items.map((item) =>
        item.id === itemId ? { ...item, enabled: !item.enabled } : item
      )
    );
  };

  return (
    <Box>
      <Card>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {title}
          </Typography>
          {canDisableTab && (
            <ListItem
              key={'disableTab'}
              primary={'Allow sites to customize these settings'}
              rightAdornment={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Checkbox
                    useSwitch
                    checked={!disableTab}
                    onChange={() => setDisableTab(!disableTab)}
                    disabled={isLoading}
                  />
                </Stack>
              }
            />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {tCommon('actions.reorder')}
          </Typography>
          <Scroller direction="column" sx={{ maxHeight: 400 }}>
            <DndSortableList<PlatformTab>
              items={items}
              setItems={(newItems) => {
                if (Array.isArray(newItems)) {
                  setItems(newItems);
                } else {
                  setItems(newItems(items));
                }
              }}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
              }}
            >
              {(item) => (
                <NavItem
                  item={item}
                  editingItemId={editingItemId}
                  setEditingItemId={setEditingItemId}
                  isLoading={isLoading}
                  onToggle={handleToggleItem}
                  tabArrayKey={tabArrayKey}
                />
              )}
            </DndSortableList>
          </Scroller>
        </Box>
      </Card>
    </Box>
  );
};

interface NavItemProps {
  item: PlatformTab;
  editingItemId: string | null;
  setEditingItemId: (id: string | null) => void;
  isLoading?: boolean;
  onToggle: (itemId: string) => void;
  tabArrayKey: string | null;
}

const NavItem: React.FC<NavItemProps> = ({
  item,
  editingItemId,
  setEditingItemId,
  isLoading,
  onToggle,
  tabArrayKey,
}) => {
  const { values } = useFormikContext<any>();
  const isEditing = editingItemId === item.id;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      setEditingItemId(null);
    } else if (e.key === 'Enter') {
      setEditingItemId(null);
    }
  };

  const getDisplayName = (item: PlatformTab) => {
    return item.name;
  };

  return (
    <ListItem
      key={item.id}
      icon={faGripDotsVertical}
      primary={
        isEditing ? (
          <FormTextField
            name={
              tabArrayKey
                ? `platformTabs.${tabArrayKey}[${(
                    values.platformTabs[tabArrayKey] || []
                  ).findIndex((tab: any) => tab.id === item.id)}].name`
                : ''
            }
            autoFocus
            size="small"
            fullWidth
            onKeyDown={handleKeyDown}
            onBlur={() => setEditingItemId(null)}
          />
        ) : (
          getDisplayName(item)
        )
      }
      rightAdornment={
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            onClick={() => setEditingItemId(item.id)}
            disabled={isLoading}
          >
            <Icon icon={faEdit} />
          </IconButton>
          <Checkbox
            useSwitch
            checked={item.enabled}
            onChange={() => onToggle(item.id)}
            disabled={isLoading}
          />
        </Stack>
      }
      sx={() => ({
        borderRadius: 1,
        ...(item.enabled
          ? {}
          : {
              opacity: 0.7,
              bgcolor: 'action.hover',
            }),
      })}
    />
  );
};
